import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Title,
  Group,
  Button,
  TextInput,
  Select,
  Paper,
  Table,
  Text,
  Code,
  Badge,
  Pagination,
  Center,
  Loader,
  Alert,
  ActionIcon,
  Tooltip,
  Modal,
} from '@mantine/core';
import { IconPlus, IconSearch, IconEye, IconTrash, IconInfoCircle } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { differenceInHours } from 'date-fns';
import { notifications } from '@mantine/notifications';
import { api } from '../services/api';
import { formatDateDistance, parseUTCDate } from '../utils/date';
import { AutoRefreshControls } from '../components/AutoRefreshControls';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

interface Database {
  id: string;
  snapshot: string;
  status: string;
  created_at: string;
  expires_at: string;
  owner_github_login?: string;
  status_message?: string;
  connection_host?: string;
  connection_port?: number;
  connection_uri?: string;
}

interface ListResponse<T> {
  items: T[];
  total: number;
}

function DatabaseStatusBadge({ status }: { status: string }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready': return 'green';
      case 'creating': return 'blue';
      case 'provisioning': return 'cyan';
      case 'deleting': return 'orange';
      case 'deleted': return 'gray';
      case 'failed': return 'red';
      default: return 'gray';
    }
  };
  
  return (
    <Badge color={getStatusColor(status)} variant="light">
      {status.toUpperCase()}
    </Badge>
  );
}

export function DatabasesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { enabled: autoRefresh, setEnabled: setAutoRefresh, refetchInterval } = useAutoRefresh({ 
    defaultEnabled: true, 
    interval: 10000 
  });
  const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false);
  const [selectedDatabase, setSelectedDatabase] = useState<Database | null>(null);
  const itemsPerPage = 50;

  // Fetch databases
  const {
    data: databasesData,
    isLoading,
    error,
  } = useQuery<ListResponse<Database>>({
    queryKey: ['databases', statusFilter],
    queryFn: () => api.listDatabases(statusFilter ? { status: statusFilter } : undefined),
    refetchInterval, // Auto-refresh every 10 seconds if enabled
  });

  // Delete database mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteDatabase(id),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Database deleted successfully',
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: ['databases'] });
      deleteModalHandlers.close();
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete database',
        color: 'red',
      });
    },
  });

  // Filter databases by search term
  const filteredDatabases = databasesData?.items?.filter((db) => {
    const matchesSearch = search
      ? db.snapshot.toLowerCase().includes(search.toLowerCase()) ||
        db.id.toLowerCase().includes(search.toLowerCase())
      : true;
    return matchesSearch;
  });

  const totalPages = Math.ceil((filteredDatabases?.length || 0) / itemsPerPage);
  const paginatedDatabases = filteredDatabases?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Safe date formatter (now using UTC-aware parsing)
  const safeFormatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    return formatDateDistance(dateString);
  };

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Container my="md">
        <Alert title="Error" color="red" icon={<IconInfoCircle />}>
          Failed to load databases: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" align="center" mb="md">
        <Title order={2}>Databases</Title>
        <Group>
          <AutoRefreshControls
            enabled={autoRefresh}
            onEnabledChange={setAutoRefresh}
            intervalLabel="(10s)"
          />
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate('/databases/create')}
          >
            Create Database
          </Button>
        </Group>
      </Group>

      <Paper p="md" withBorder>
        <Group gap="md">
          <TextInput
            placeholder="Search databases..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by status"
            clearable
            value={statusFilter}
            onChange={setStatusFilter}
            data={[
              { value: 'ready', label: 'Ready' },
              { value: 'creating', label: 'Creating' },
              { value: 'failed', label: 'Failed' },
              { value: 'deleting', label: 'Deleting' },
            ]}
            style={{ width: 200 }}
          />
        </Group>
      </Paper>

      <Paper withBorder mt="md">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Database Name</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Expires</Table.Th>
              <Table.Th>Owner</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {paginatedDatabases?.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text ta="center" c="dimmed" py="xl">
                    {search || statusFilter
                      ? 'No databases match your filters'
                      : 'No databases yet. Create one to get started!'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              paginatedDatabases?.map((db) => {
                const expiresAt = parseUTCDate(db.expires_at);
                const hoursUntilExpiry = expiresAt
                  ? differenceInHours(expiresAt, new Date())
                  : null;
                const isExpiringSoon =
                  hoursUntilExpiry !== null && hoursUntilExpiry < 24 && hoursUntilExpiry > 0;
                const isExpired = hoursUntilExpiry !== null && hoursUntilExpiry <= 0;

                return (
                  <Table.Tr
                    key={db.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/databases/${db.id}`)}
                  >
                    <Table.Td>
                      <div>
                        <Text fw={500}>{(db as any).name || db.id}</Text>
                        {db.status_message && (
                          <Text size="xs" c="dimmed">
                            {db.status_message}
                          </Text>
                        )}
                      </div>
                    </Table.Td>
                    <Table.Td>
                      <DatabaseStatusBadge status={db.status} />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{safeFormatDate(db.created_at)}</Text>
                    </Table.Td>
                    <Table.Td>
                      {expiresAt ? (
                        <Badge
                          color={isExpired ? 'red' : isExpiringSoon ? 'yellow' : 'gray'}
                          variant="light"
                        >
                          {isExpired
                            ? 'Expired'
                            : safeFormatDate(db.expires_at)}
                        </Badge>
                      ) : (
                        <Text c="dimmed">—</Text>
                      )}
                    </Table.Td>
                    <Table.Td>{db.owner_github_login || '—'}</Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Group gap="xs" wrap="nowrap">
                        <Tooltip label="View Details">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => navigate(`/databases/${db.id}`)}
                          >
                            <IconEye size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => {
                              setSelectedDatabase(db);
                              deleteModalHandlers.open();
                            }}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>

        {totalPages > 1 && (
          <Group justify="center" p="md">
            <Pagination
              total={totalPages}
              value={currentPage}
              onChange={setCurrentPage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        )}
      </Paper>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={deleteModalHandlers.close}
        title="Delete Database"
      >
        <Text>
          Are you sure you want to delete database <strong>{selectedDatabase?.snapshot}</strong>?
        </Text>
        <Text size="sm" c="dimmed" mt="sm">
          This action cannot be undone. The database and all its data will be permanently deleted.
        </Text>

        <Group justify="flex-end" mt="xl">
          <Button variant="subtle" onClick={deleteModalHandlers.close}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={() => selectedDatabase && deleteMutation.mutate(selectedDatabase.id)}
            loading={deleteMutation.isPending}
          >
            Delete Database
          </Button>
        </Group>
      </Modal>
    </Container>
  );
}

